/**
 * Location type definitions for Idle Depths.
 *
 * The game world is structured as a graph of connected locations.
 * Travel uses Dijkstra's algorithm for shortest path calculation.
 */

import type { CharacterData } from './character.js';

// ============================================================================
// Unlock System
// ============================================================================

/**
 * Context provided to unlock check functions.
 *
 * Contains all game state needed to determine if a location is accessible.
 * This is passed to the `checkUnlock` function on LocationDefinition.
 */
export interface UnlockContext {
  /** Current character data */
  character: CharacterData;

  /** Items in bank (itemId -> quantity) */
  bankItems: Map<string, number>;

  /** User's DLC entitlements */
  entitlements: string[];

  /** Visited location IDs (for discovery-based unlocks) */
  visitedLocations: Set<string>;

  // Future additions:
  // completedQuests: Set<string>;
  // skillLevels: Map<string, number>;
}

/**
 * Function signature for location unlock checks.
 *
 * @param context - Game state context
 * @returns Object with unlock status and optional reason if locked
 */
export type UnlockCheckFunction = (context: UnlockContext) => UnlockResult;

/**
 * Result of an unlock check.
 */
export interface UnlockResult {
  /** Whether the location is unlocked */
  unlocked: boolean;

  /** Reason displayed to player if locked (e.g., "Requires completing Quest X") */
  reason?: string;
}

/**
 * Location unlock requirements (data-driven approach).
 *
 * Simple requirements can be expressed as data. For complex requirements,
 * use the `checkUnlock` function on LocationDefinition instead.
 */
export interface LocationUnlockRequirements {
  /** Quest IDs that must be completed */
  quests?: string[];

  /** Skill levels required (skillId: minLevel) */
  skills?: Record<string, number>;

  /** Item IDs that must be in bank/inventory */
  items?: string[];

  /** DLC entitlement required (null = free, 'premium_unlock' = paid) */
  dlc?: string | null;
}

// ============================================================================
// Location Graph
// ============================================================================

/**
 * Connection between two locations (directed edge in the graph).
 */
export interface LocationConnection {
  /** Target location ID */
  toLocationId: string;

  /** Travel time in milliseconds */
  travelTimeMs: number;

  /** Whether this connection is unlocked (shortcuts via quests - future) */
  isUnlocked?: boolean;
}

/**
 * Location definition registered via content packs.
 *
 * Defines the static properties of a location in the game world.
 */
export interface LocationDefinition {
  /** Unique location identifier */
  id: string;

  /** Display name */
  name: string;

  /** Full description */
  description: string;

  /** Icon identifier (Lucide icon name) */
  icon: string;

  /** Visual theme identifier */
  theme: 'coastal' | 'forest' | 'mountain' | 'ruins' | 'dungeon';

  /** Static unlock requirements (simple cases) */
  unlockRequirements: LocationUnlockRequirements;

  /**
   * Custom unlock check function (complex cases).
   *
   * If provided, this function is called with game state context
   * to determine if the location is accessible. Overrides unlockRequirements.
   *
   * @example
   * ```typescript
   * checkUnlock: (ctx) => ({
   *   unlocked: ctx.character.stats.get('level')?.value >= 10,
   *   reason: 'Requires Level 10'
   * })
   * ```
   */
  checkUnlock?: UnlockCheckFunction;

  /** Direct connections to other locations */
  connections: LocationConnection[];

  /** Whether this is the starting location */
  isStartingLocation?: boolean;
}

// ============================================================================
// Travel Route
// ============================================================================

/**
 * A calculated travel route between two locations.
 *
 * Created by the pathfinding algorithm when starting travel.
 */
export interface TravelRoute {
  /** Starting location ID */
  fromLocationId: string;

  /** Destination location ID */
  toLocationId: string;

  /** Ordered list of location IDs to travel through (includes from and to) */
  path: string[];

  /** Total travel time in milliseconds */
  totalTimeMs: number;

  /** Time per leg in milliseconds (indexed by leg number, length = path.length - 1) */
  legTimesMs: number[];
}

// ============================================================================
// Travel Activity Data
// ============================================================================

/**
 * Travel state stored in character activity data.
 *
 * Contains all information needed to track and resume travel progress.
 *
 * Note: Index signature added for Record<string, unknown> compatibility
 * when serializing to server. This allows the interface to be assigned
 * to Record<string, unknown> while maintaining type safety for known properties.
 */
export interface TravelActivityData {
  /** Index signature for serialization compatibility */
  [key: string]: unknown;

  /** The calculated route */
  route: TravelRoute;

  /** When travel started (Unix timestamp ms) */
  startedAt: number;

  /** When travel will complete (Unix timestamp ms) */
  endsAt: number;

  /** Current leg index (0-based) */
  currentLegIndex: number;

  /** Progress through current leg (0-1) */
  currentLegProgress: number;
}

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Type guard for TravelActivityData.
 *
 * @param data - Unknown value to check
 * @returns True if data is valid TravelActivityData
 */
export function isTravelActivityData(data: unknown): data is TravelActivityData {
  return (
    typeof data === 'object' &&
    data !== null &&
    'route' in data &&
    'startedAt' in data &&
    'endsAt' in data &&
    'currentLegIndex' in data &&
    'currentLegProgress' in data
  );
}

/**
 * Type guard for LocationDefinition.
 *
 * @param item - Unknown value to check
 * @returns True if item is a valid LocationDefinition
 */
export function isLocationDefinition(item: unknown): item is LocationDefinition {
  return (
    typeof item === 'object' &&
    item !== null &&
    'id' in item &&
    typeof (item as LocationDefinition).id === 'string' &&
    'name' in item &&
    typeof (item as LocationDefinition).name === 'string' &&
    'description' in item &&
    typeof (item as LocationDefinition).description === 'string' &&
    'connections' in item &&
    Array.isArray((item as LocationDefinition).connections)
  );
}
